import Check from "@mui/icons-material/Check";
import { Button, TextField, useTheme } from "@mui/material";
import { Dialog } from "components/Dialog";
import { ActivityStatus } from "generated-graphql/graphql";
import { useState } from "react";
import { ActivityButtonProps } from ".";
import { useUpdateActivity } from "../../../../../../hooks/activities";
import { useReportDetails } from "../../useReportDetails";

export const CompleteActivity = ({
  activity,
  showWarningPanel = false,
  context = "report",
  tags,
  refetchQueries,
}: ActivityButtonProps & {
  showWarningPanel?: boolean;
  context?: "report" | "cohort";
  tags?: string;
  refetchQueries?: string[];
}) => {
  const [isCompletionDialogOpen, setIsCompletionDialogOpen] = useState(false);

  const { data } = useReportDetails();
  const [updateActivity, { loading: isActivityUpdating }] = useUpdateActivity(refetchQueries);

  const contextMessage =
    context === "cohort" ? "cohort activities page" : "report details page";
  const tagsToUse = tags || data?.tierIIReport.tags;

  const submitCompletion = async () => {
    await updateActivity({
      variables: {
        id: activity.id,
        input: {
          status: ActivityStatus.StaffCompleted,
          message: `Manually completed activity from ${contextMessage}.${
            tagsToUse ? ` Clearing tags: ${tagsToUse}.` : ""
          }`,
        },
      },
    });
  };

  const onClickComplete = () => {
    if (showWarningPanel) {
      setIsCompletionDialogOpen(true);
    } else {
      submitCompletion();
    }
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        color="primary"
        loading={isActivityUpdating}
        loadingPosition="start"
        startIcon={<Check />}
        onClick={() => onClickComplete()}
        sx={{ minWidth: 150 }}
      >
        Complete activity
      </Button>
      <CompletionDialog
        activityId={activity.id}
        onSubmit={submitCompletion}
        isSubmitting={isActivityUpdating}
        isOpen={isCompletionDialogOpen}
        onCloseDialog={() => setIsCompletionDialogOpen(false)}
      />
    </>
  );
};
interface Props {
  activityId: string;
  isOpen: boolean;
  onCloseDialog: () => void;
  onSubmit: (message?: string | undefined) => void;
  isSubmitting: boolean;
}

const CompletionDialog = ({
  isOpen,
  onCloseDialog,
  onSubmit,
  isSubmitting,
}: Props) => {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const disableSubmit = name === "" || description === "";

  const onClose = () => {
    setName("");
    setDescription("");
    onCloseDialog();
  };

  const handleSubmit = () => {
    onSubmit(`Completed by ${name} - ${description}`);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      fullWidth
      title="Manually Complete Activity"
      onClose={onClose}
      primaryActionButtonProps={{
        disabled: disableSubmit,
        loading: isSubmitting,
        onClick: handleSubmit,
        saveText: "Complete",
        startIcon: null,
      }}
      description="Please describe why it is safe to manually complete this activity."
    >
      <TextField
        margin="dense"
        id="name"
        label="Name"
        type="text"
        fullWidth
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextField
        margin="dense"
        multiline
        rows={4}
        id="description"
        label="Description"
        type="text"
        fullWidth
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </Dialog>
  );
};
